/**
 * Validazione "gate" dei dati cliente prima di creare un link di firma o
 * caricare un contratto. La lista dei campi obbligatori è configurabile per
 * organizzazione in `settings.required_client_fields_for_contract`.
 *
 * Token supportati e relativo controllo sul cliente:
 *  - business_name        → Ragione Sociale
 *  - vat_number           → Partita IVA
 *  - tax_code             → Codice Fiscale
 *  - sdi_code             → Codice SDI
 *  - pec                  → PEC
 *  - email                → Email
 *  - email_amministrazione→ Mail amministrativa (campo dedicato su clients)
 *  - address              → Indirizzo completo (via + CAP + città + provincia)
 *  - whatsapp_group       → Numero/i WhatsApp cliente + nome gruppo (dati a
 *                           livello DEAL: `whatsapp_group_numbers` jsonb e
 *                           `whatsapp_group_name` text — il gate hook li
 *                           inietta nel record prima della validazione)
 */

import { isRecognizedForeignCountry } from './countries';

export type RequiredClientField =
  | 'business_name'
  | 'vat_number'
  | 'tax_code'
  | 'sdi_code'
  | 'pec'
  | 'email'
  | 'email_amministrazione'
  | 'address'
  | 'whatsapp_group';

/** Etichette leggibili per i messaggi all'utente. */
export const CLIENT_FIELD_LABELS: Record<RequiredClientField, string> = {
  business_name: 'Ragione Sociale',
  vat_number: 'Partita IVA',
  tax_code: 'Codice Fiscale',
  sdi_code: 'Codice SDI',
  pec: 'PEC',
  email: 'Email',
  email_amministrazione: 'Mail amministrativa',
  address: 'Indirizzo completo (via, CAP, città, provincia)',
  whatsapp_group: 'Numero/i WhatsApp cliente + nome gruppo',
};

/** Ordine di visualizzazione nelle impostazioni. */
export const REQUIRED_CLIENT_FIELD_ORDER: RequiredClientField[] = [
  'business_name',
  'vat_number',
  'tax_code',
  'sdi_code',
  'pec',
  'email',
  'email_amministrazione',
  'address',
  'whatsapp_group',
];

export const DEFAULT_REQUIRED_CLIENT_FIELDS: RequiredClientField[] = [
  'business_name',
  'vat_number',
  'email_amministrazione',
  'address',
];

const isFilled = (v: unknown): boolean =>
  v !== null && v !== undefined && String(v).trim() !== '';

/** Indirizzo "completo" = via + CAP + città + provincia tutti valorizzati. */
function isAddressComplete(client: Record<string, any>): boolean {
  return (
    isFilled(client.street_address) &&
    isFilled(client.postal_code) &&
    isFilled(client.city) &&
    isFilled(client.province)
  );
}

function fieldFilled(client: Record<string, any>, field: RequiredClientField): boolean {
  if (field === 'address') return isAddressComplete(client);
  if (field === 'email_amministrazione') {
    // Campo dedicato; fallback su un eventuale contatto area=amministrazione se passato
    if (isFilled(client.email_amministrazione)) return true;
    const contacts = (client.client_contacts as any[] | undefined) ?? [];
    return contacts.some((c) => c?.area === 'amministrazione' && isFilled(c?.email));
  }
  if (field === 'whatsapp_group') {
    // I dati risiedono sul DEAL e vengono iniettati nel record dai caller
    // (useContractClientGate, signatureStore, ClientRequiredFieldsDrawer).
    // Riuso di un gruppo esistente (whatsapp_reuse_group_jid): il gruppo c'è già,
    // numeri e nome non sono richiesti.
    if (isFilled(client.whatsapp_reuse_group_jid)) return true;
    const nums = client.whatsapp_group_numbers;
    const hasNumber =
      Array.isArray(nums) &&
      nums.filter((n: unknown) => String(n ?? '').trim() !== '').length >= 1;
    return hasNumber && isFilled(client.whatsapp_group_name);
  }
  return isFilled(client[field]);
}

export interface ClientValidationResult {
  isValid: boolean;
  missingFields: RequiredClientField[];
  missingLabels: string[];
}

/**
 * Token che hanno senso SOLO per i clienti italiani: per un cliente estero
 * riconosciuto (clients.country in lista e diverso dall'Italia) vengono
 * esonerati automaticamente dal gate — SDI, PEC e Codice Fiscale non esistono
 * fuori dall'Italia e bloccherebbero il contratto per sempre.
 */
export const ITALY_ONLY_CONTRACT_FIELDS: RequiredClientField[] = ['sdi_code', 'pec', 'tax_code'];

/**
 * Lista dei campi richiesti effettivi in base al paese del cliente.
 * Default conservativo: paese vuoto o non riconosciuto = regole italiane.
 */
export function effectiveRequiredFieldsForCountry(
  requiredFields: string[] | null | undefined,
  country: string | null | undefined,
): string[] {
  const fields = [...(requiredFields ?? [])];
  if (!isRecognizedForeignCountry(country)) return fields;
  return fields.filter((f) => !ITALY_ONLY_CONTRACT_FIELDS.includes(f as RequiredClientField));
}

/**
 * Valida il cliente contro la lista di campi obbligatori.
 * Se requiredFields è vuoto/undefined → nessun blocco (isValid=true).
 */
export function validateClientForContract(
  client: Record<string, any> | null | undefined,
  requiredFields: string[] | null | undefined,
): ClientValidationResult {
  const fields = (requiredFields ?? []).filter(
    (f): f is RequiredClientField => f in CLIENT_FIELD_LABELS,
  );
  if (!client || fields.length === 0) {
    return { isValid: true, missingFields: [], missingLabels: [] };
  }
  const missing = fields.filter((f) => !fieldFilled(client, f));
  return {
    isValid: missing.length === 0,
    missingFields: missing,
    missingLabels: missing.map((f) => CLIENT_FIELD_LABELS[f]),
  };
}
